import { inject } from "@angular/core";
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from "@angular/common/http";
import { Router } from "@angular/router";
import { NGXLogger } from "ngx-logger";
import { AUTH_SVC, AuthService } from "@company-box/services";
import { EMPTY, Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { LoginModalService } from "../services/login-modal.service";

/**
 * On a 401 from any backend call, the session has expired. Show the login
 * modal over the current page instead of letting the error reach the
 * caller, which would otherwise show the generic error modal.
 *
 * Excludes the /oauth/token endpoint itself (bad-credentials 401s during a
 * login attempt are not a session expiry) and the /login and /login-redesign
 * pages (they have their own full-page login form; opening the modal on top
 * of them would be redundant).
 */
export const sessionExpiredInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const router = inject(Router);
  const authService = inject(AUTH_SVC) as AuthService;
  const loginModalService = inject(LoginModalService);
  const logger = inject(NGXLogger);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        !req.url.includes("/oauth/token") &&
        !onLoginPage(router.url)
      ) {
        logger.debug("[sessionExpiredInterceptor] session expired; showing login modal");
        authService.clearToken();
        loginModalService.open(router.url);
        return EMPTY;
      }
      return throwError(() => error);
    })
  );
};

const onLoginPage = (url: string): boolean => {
  const path = url.split(/[?#]/)[0];
  return ["/login", "/login-redesign"].some((page) => path === page || path.startsWith(`${page}/`));
};
